import Service from '@root/common/base/BaseService';
import { DocumentNode, OperationVariables } from '@apollo/client/core';

import { DeleteCustomerData, GetCustomerData, CreateCustomerData, UpdateCustomerData } from '@root/modules/customer/types/customer';

import getCustomerDataQuery from '@root/modules/customer/graphql/getCustomerData.graphql';
import createCustomerDataQuery from '@root/modules/customer/graphql/createCustomerData.graphql';
import deleteCustomerDataQuery from '@root/modules/customer/graphql/deleteCustomerData.graphql';
import updateCustomerDataQuery from '@root/modules/customer/graphql/updateCustomerData.graphql';

type DeleteServiceVariables = DeleteCustomerData['variables'];
type GetServiceVariables = GetCustomerData['variables'];
type CreateServiceVariables = CreateCustomerData['variables'];
type UpdateServiceVariables = UpdateCustomerData['variables'];

export default class CustomerDataService extends Service {
  private _customerToken: string | null = null;

  public setCustomerToken(customerToken: string) {
    this._customerToken = customerToken;
  }

  private async fetch<Response, Request extends OperationVariables>(variables: Request, query: DocumentNode) {
    if (!this._customerToken) {
      return;
    }

    const apiProvider = this.createProvider('GraphQL');
    apiProvider.selectAPI('customer-data-api');

    const queryOptions = {
      variables,
      context: {
        headers: {
          Authorization: `Bearer ${this._customerToken}`,
        },
      },
    };

    const options = Object.assign({ query }, queryOptions);
    const response = await apiProvider.request<Response>(options);

    return response;
  }

  public async delete(variables: DeleteServiceVariables) {
    const response = await this.fetch<DeleteCustomerData['data'], DeleteServiceVariables>(variables, deleteCustomerDataQuery);

    return response;
  }

  public async create(variables: CreateServiceVariables) {
    const response = await this.fetch<CreateCustomerData['data'], CreateServiceVariables>(variables, createCustomerDataQuery);

    return response;
  }

  public async get(variables: GetServiceVariables) {
    const response = await this.fetch<GetCustomerData['data'], GetServiceVariables>(variables, getCustomerDataQuery);

    return response;
  }

  public async update(variables: UpdateServiceVariables) {
    const response = await this.fetch<CreateCustomerData['data'], UpdateServiceVariables>(variables, updateCustomerDataQuery);

    return response;
  }
}
